import { buildJourneysListParams } from '../lib';

// Request creators for the 13 Journey Engine endpoints. Every response
// is wrapped in { data, correlationUUID } — the shared client already
// unwraps `data` into `payload`. Bare-200 endpoints (validateOnly,
// events, overrides) carry no `data`, so their payload is undefined.
// All enum strings on this API are lowercase-first.

const toQuery = params =>
  Object.entries(params)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');

// ---- Definitions ----

// Create (no uuid in body) or full-replace (uuid present). Only used
// for creation and for `validateOnly` dry runs — edits go through
// patchDefinitionAction so the server's draft-only guard applies.
export const saveDefinitionAction = ({ accountUuid, definition, validateOnly }) => ({
  method: 'PUT',
  endpoint: `/accounts/${accountUuid}/program-definitions${
    validateOnly ? '?validateOnly=true' : ''
  }`,
  body: definition,
});

export const getDefinitionAction = ({ accountUuid, definitionUuid }) => ({
  method: 'GET',
  endpoint: `/accounts/${accountUuid}/program-definitions/${definitionUuid}`,
});

/**
 * Templates the given account may START a journey from — its own,
 * the global ones and its distribution sponsor's — in the scope the
 * start endpoint itself uses (`RunnableByAccount`). The plain list
 * below is `OwnedByAccount`, which leaves a child clinic without its
 * sponsor's templates. ASSUMED CONTRACT (2026-09-10): `?runnable=true`
 * on the existing list; adjust this one line when the backend lands.
 */
export const listStartableDefinitionsAction = ({ accountUuid }) => ({
  method: 'GET',
  endpoint: `/accounts/${accountUuid}/program-definitions?runnable=true`,
});

// Not paginated — returns the account's own definitions plus the sponsor
// definitions listed in its `allowedPrograms`; a sponsor asking for its
// own account gets its whole catalogue (drafts included).
export const listDefinitionsAction = ({ accountUuid }) => ({
  method: 'GET',
  endpoint: `/accounts/${accountUuid}/program-definitions`,
});

// JSON Patch (RFC 6902). Draft-only: non-draft or non-owned rows 409.
export const patchDefinitionAction = ({ accountUuid, definitionUuid, patches }) => ({
  method: 'PATCH',
  endpoint: `/accounts/${accountUuid}/program-definitions/${definitionUuid}`,
  body: patches,
});

// Draft-only on the server: a published or archived definition, or one
// this account does not own, is refused. Deleting a draft that no
// journey has ever started is the only supported case — the UI mirrors
// that by offering Delete on drafts alone.
export const deleteDefinitionAction = ({ accountUuid, definitionUuid }) => ({
  method: 'DELETE',
  endpoint: `/accounts/${accountUuid}/program-definitions/${definitionUuid}`,
});

// Idempotent: same uuid per account, existing row returned untouched.
export const seedMetabolicLessonsAction = ({ accountUuid }) => ({
  method: 'POST',
  endpoint: `/accounts/${accountUuid}/program-definitions/seed/metabolic-lessons`,
});

// ---- Journeys ----

// Enroll a patient. The entry step is sent synchronously inside this
// call. 400 = AlreadyRunning (same patient+definition), 404 = missing
// OR unpublished definition (deliberately indistinguishable).
export const startJourneyAction = ({ accountUuid, programDefinitionUUID, patientUUID }) => ({
  method: 'PUT',
  endpoint: `/accounts/${accountUuid}/program-journeys`,
  body: { programDefinitionUUID, patientUUID, programUUID: null },
});

export const getJourneyAction = ({ accountUuid, journeyUuid }) => ({
  method: 'GET',
  endpoint: `/accounts/${accountUuid}/program-journeys/${journeyUuid}`,
});

export const listJourneysAction = ({ accountUuid, ...filters }) => ({
  method: 'GET',
  endpoint: `/accounts/${accountUuid}/program-journeys?${toQuery(
    buildJourneysListParams(filters)
  )}`,
});

// ---- Events ----

// Intentionally unused by the dashboard UI: survey/resource completions
// already emit events inside the backend, and a manually posted event
// matches by type (not instance) so it can advance unrelated journeys.
// Kept for API-surface completeness / support tooling.
export const reportEventAction = ({ accountUuid, eventType, patientUUID, payload, dedupKey }) => ({
  method: 'PUT',
  endpoint: `/accounts/${accountUuid}/program-events`,
  body: { eventType, patientUUID, payload, dedupKey },
});

// ---- Overrides ----

// override: 'pause' | 'resume' | 'skip-step' | 'retry-step'. Bare 200 on
// success — callers must re-fetch journey state. 409 means a concurrent
// mutation won and the op did NOT apply.
export const journeyOverrideAction = ({ accountUuid, journeyUuid, override, reason }) => ({
  method: 'POST',
  endpoint: `/accounts/${accountUuid}/program-journeys/${journeyUuid}/${override}`,
  ...(reason ? { body: { reason } } : {}),
});
